import { Stack } from "@mui/material";
import React, { type FC, type ReactNode, useCallback, useMemo } from "react";
import { useNavigate } from "react-router";

import { ROUTES } from "~/constants/routing";
import { PremiumFeatureTooltip } from "~/feature/subscription/components/PremiumFeatureTooltip";
import { useSubscriptionGuard } from "~/feature/subscription/components/SubscriptionGuard/useSubscriptionGuard";
import { FeatureKey } from "~/types/subscription";

import * as Styled from "./SubscriptionGuard.styles";
import { getPremiumFeatureDescription } from "./subscriptionGuard.utils";

interface ISubscriptionGuardProps {
  children: ReactNode;
  featureKey: FeatureKey;
}

export const SubscriptionGuard: FC<ISubscriptionGuardProps> = ({
  children,
  featureKey,
}) => {
  const navigate = useNavigate();
  const { isLocked, availableOnPlan } = useSubscriptionGuard(featureKey);
  const pricingUrl = useMemo(
    () => ROUTES.pricing.createURL(featureKey),
    [featureKey]
  );

  const handleNavigateToPricingPage = useCallback(() => {
    navigate(pricingUrl);
  }, [navigate, pricingUrl]);

  const handleClickCapture = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
    },
    []
  );

  const handleKeyDownCapture = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        event.stopPropagation();
        handleNavigateToPricingPage();
      }
    },
    [handleNavigateToPricingPage]
  );

  const description = getPremiumFeatureDescription(featureKey, availableOnPlan);

  if (!isLocked) {
    return children;
  }

  return (
    <Styled.Root>
      <PremiumFeatureTooltip
        description={description}
        pricingUrl={pricingUrl}
      >
        <Stack
          onClickCapture={handleClickCapture}
          onKeyDownCapture={handleKeyDownCapture}
          sx={{
            flexDirection: "row",
            gap: 0.2,
            alignItems: "center",
            flexGrow: 1,
            maxWidth: "100%"
          }}>
          {children}
        </Stack>
      </PremiumFeatureTooltip>
    </Styled.Root>
  );
};
